import { ReceiptTextWithBadgeCheckIcon } from '@/components/atoms/icons/ReceiptTextWithBadgeCheckIcon';
import { Typography } from '@/components/atoms/Typography';
import { useTranslation, useUIColors } from '@/core/hooks';
import React from 'react';
import { StyleSheet, View } from 'react-native';

export interface PaymentsEmptyStateProps {
    /**
     * Type of payment to show in the message (e.g., 'property', 'community')
     * Used to customize the empty state message
     */
    type?: 'property' | 'community';
    /**
     * Variant of empty state (e.g., 'upcoming', 'history')
     * Used to show different messages and icons
     */
    variant?: 'upcoming' | 'history';
}

/**
 * Reusable empty state component for payments
 * Displays when there are no payments to show
 * Can be used for both property and community payment tabs, and for upcoming/history
 */
export function PaymentsEmptyState({ type = 'property', variant = 'upcoming' }: PaymentsEmptyStateProps) {
    const { t } = useTranslation();
    const colors = useUIColors();

    // Get title and subtitle based on variant
    const title = variant === 'history'
        ? t('payments.historyEmptyStateTitle' as any)
        : t(`payments.emptyStateTitle${type === 'property' ? 'Property' : 'Community'}` as any);

    const subtitle = variant === 'history'
        ? t('payments.historyEmptyStateSubtitle' as any)
        : t('payments.emptyStateSubtitle' as any);

    return (
        <View style={styles.container}>
            <View style={styles.iconContainer}>
                <ReceiptTextWithBadgeCheckIcon size={80} color={colors.fontTertiary} />
            </View>
            <Typography variant="title2" color="primary" style={styles.title}>
                {title}
            </Typography>
            <Typography variant="body" color="primary" style={styles.subtitle}>
                {subtitle}
            </Typography>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 24,
        paddingHorizontal: 24,
        gap: 32,
    },
    iconContainer: {
        width: 88,
        height: 88,
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        textAlign: 'center',
    },
    subtitle: {
        textAlign: 'center',
    },
});

export default PaymentsEmptyState;

